import { useState, useRef, useCallback, useEffect } from 'react'
import useRequestState from './useRequestState'

/**
 * This hook will automatically call that function on initial render and any time
 * the given function is updated (recommended it be wrapped in a useCallback).
 * @param {func} endpointFunction - Function that calls an endpoint internally. Must return a promise.
 * @returns [response, refetch, requestState]
 */
export default function useDataEndpoint(endpointFunction) {
  const [response, setResponse] = useState(null)
  const [func, requestState] = useRequestState(endpointFunction)
  const promiseRef = useRef(null)
  let isMounted = true
  const runRequest = useCallback(
    (...args) => {
      const promise = func(...args)
        .then((newResponse) => {
          if (promiseRef.current !== promise) return
          isMounted && setResponse(newResponse)
        })
        .catch((error) => {
          console.error(`Error found: `, error)
          isMounted && setResponse(null)
        })
      promiseRef.current = promise
    },
    [func, isMounted]
  )

  useEffect(() => {
    runRequest()
    return () => {
      isMounted = false
    }
  }, [runRequest])

  return [response, runRequest, requestState]
}
